/**
 * GsplatRevealBloom - Bloom burst reveal effect for gaussian splats
 *
 * A fast, punchy reveal where an expanding shockwave blooms splats into
 * existence. Each splat pops from a tiny glowing dot to slightly larger
 * than life, then snaps back to its true size. Creates a satisfying
 * burst-and-settle feel.
 *
 * Uses the same external property interface as the radial reveal
 * (center, speed, acceleration, etc.) so it's a drop-in replacement.
 *
 * Shader functions use PlayCanvas 2.16+ API:
 * - modifySplatCenter, modifySplatRotationScale, modifySplatColor
 * - gsplatGetSizeFromScale, gsplatMakeSpherical
 */
import * as pc from 'playcanvas';
import { relightingSnippetGLSL, relightingSnippetWGSL } from './gsplat-relighting';
import { mirrorClipSnippetGLSL, mirrorClipSnippetWGSL } from './gsplat-mirror-clip';

const bloomShaderGLSL = /* glsl */ `
uniform float uTime;
uniform vec3 uCenter;
uniform float uWaveSpeed;
uniform float uAcceleration;
uniform float uBandWidth;
uniform float uOvershoot;     // e.g. 1.2 = 20% overshoot
uniform vec3 uBloomTint;      // warm color at wavefront
uniform vec3 uSettleTint;     // cool color trailing behind
uniform float uWarpedRadius;  // warped max scene radius for reverse-wave

// Shared per-splat state
float g_dist;
float g_bloom;   // <0.01 = hidden, 0..1 = blooming, 1..overshoot = overshoot, settled = 1.0

float hash(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

void initBloom(vec3 center) {
    float rawDist = length(center - uCenter);

    // Distance warp: compress far distances so distant splats reveal sooner,
    // while preserving near-field pacing. Formula saturates smoothly as rawDist grows:
    //   warped = raw / sqrt(1 + raw/K)
    // With K=5: near rawDist ~10 → ~4.5, rawDist ~500 → ~49 (~10x compression).
    // This makes the reveal wave reach distant horizon splats ~10x sooner.
    const float K = 5.0;
    g_dist = rawDist / sqrt(1.0 + rawDist / K);

    // Wave position with acceleration
    float wavePos = uWaveSpeed * uTime + 0.5 * uAcceleration * uTime * uTime;

    // Per-splat jitter so the wavefront isn't a perfect circle
    float jitter = (hash(center) - 0.5) * uBandWidth * 0.4;
    float effectiveDist = g_dist + jitter;

    // --- Forward wave (inside-out) ---
    float forwardBloom;
    if (effectiveDist > wavePos + uBandWidth * 0.5) {
        forwardBloom = 0.0;
    } else if (effectiveDist > wavePos - uBandWidth * 0.5) {
        forwardBloom = smoothstep(wavePos + uBandWidth * 0.5, wavePos - uBandWidth * 0.5, effectiveDist);
    } else {
        float distBehind = (wavePos - uBandWidth * 0.5) - effectiveDist;
        float settleTime = distBehind / max(uWaveSpeed + uAcceleration * uTime, 1.0);
        float overshootDecay = exp(-settleTime * 6.0);
        forwardBloom = 1.0 + (uOvershoot - 1.0) * overshootDecay;
    }

    // --- Reverse wave (outside-in) for outer 10% of splats ---
    // Starts at time 0 from the scene edge, sweeping inward simultaneously.
    // Only affects splats in the outer 10% of warped distance.
    float reverseBloom = 0.0;
    float outerThreshold = uWarpedRadius * 0.9;
    if (g_dist > outerThreshold && uWarpedRadius > 0.0) {
        float distFromEdge = uWarpedRadius - effectiveDist;
        // Reverse wave also uses the same speed/accel
        float revWavePos = uWaveSpeed * uTime + 0.5 * uAcceleration * uTime * uTime;
        if (distFromEdge < revWavePos + uBandWidth * 0.5) {
            if (distFromEdge > revWavePos - uBandWidth * 0.5) {
                reverseBloom = smoothstep(revWavePos + uBandWidth * 0.5, revWavePos - uBandWidth * 0.5, distFromEdge);
            } else {
                float distBehind = (revWavePos - uBandWidth * 0.5) - distFromEdge;
                float settleTime = distBehind / max(uWaveSpeed + uAcceleration * uTime, 1.0);
                float overshootDecay = exp(-settleTime * 6.0);
                reverseBloom = 1.0 + (uOvershoot - 1.0) * overshootDecay;
            }
        }
    }

    // Take whichever wave reveals first
    g_bloom = max(forwardBloom, reverseBloom);
}

void modifySplatCenter(inout vec3 center) {
    initBloom(center);
    rl_casterCenter(center);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    rl_recordSplatShape(rotation, scale);
    mirrorClipSplat(modifiedCenter, scale);
    if (g_bloom < 0.01) {
        scale = vec3(0.0);
        return;
    }

    // Fully settled
    if (g_bloom >= 0.99 && g_bloom <= 1.01) {
        return;
    }

    float origSize = gsplatGetSizeFromScale(scale);

    if (g_bloom <= 1.0) {
        // Bloom phase: transition from dot to full size
        float t = g_bloom;

        if (t < 0.15) {
            // Tiny spherical spark
            float dotSize = min(t * 0.4 * origSize, origSize);
            gsplatMakeSpherical(scale, dotSize);
        } else if (t < 0.5) {
            // Growing spherical dot
            float size = mix(0.06, 0.5, (t - 0.15) / 0.35) * origSize;
            gsplatMakeSpherical(scale, min(size, origSize));
        } else {
            // Transitioning from spherical to original shape
            float morphT = (t - 0.5) / 0.5; // 0→1
            float sizeFactor = mix(0.5, 1.0, morphT);
            scale *= sizeFactor;
        }
    } else {
        // Overshoot phase: slightly larger than original
        scale *= g_bloom;
    }
}

void bloomColorEffect(vec3 center, inout vec4 color) {
    if (g_bloom < 0.01) return;

    if (g_bloom <= 1.0) {
        // Bloom phase: bright tint, strongest at wavefront (g_bloom 0.2-0.6)
        float waveFrontIntensity = sin(clamp(g_bloom, 0.0, 1.0) * 3.14159);
        color.rgb += uBloomTint * waveFrontIntensity * 0.5;
    } else if (g_bloom > 1.005) {
        // Overshoot phase: brief cool flash as splat settles
        float overshootAmount = (g_bloom - 1.0) / max(uOvershoot - 1.0, 0.01);
        color.rgb += uSettleTint * overshootAmount * 0.3;
    }
}

void modifySplatColor(vec3 center, inout vec4 color) {
    // EFFECT_HOOK
    bloomColorEffect(center, color);
}
`;

const bloomShaderWGSL = /* wgsl */ `
uniform uTime: f32;
uniform uCenter: vec3f;
uniform uWaveSpeed: f32;
uniform uAcceleration: f32;
uniform uBandWidth: f32;
uniform uOvershoot: f32;
uniform uBloomTint: vec3f;
uniform uSettleTint: vec3f;
uniform uWarpedRadius: f32;

var<private> g_dist: f32;
var<private> g_bloom: f32;

fn hash(p: vec3f) -> f32 {
    return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

fn initBloom(center: vec3f) {
    let rawDist = length(center - uniform.uCenter);

    // Distance warp: compress far distances so distant splats reveal sooner,
    // while preserving near-field pacing. See GLSL version for the derivation.
    let K: f32 = 5.0;
    g_dist = rawDist / sqrt(1.0 + rawDist / K);

    let wavePos = uniform.uWaveSpeed * uniform.uTime + 0.5 * uniform.uAcceleration * uniform.uTime * uniform.uTime;

    let jitter = (hash(center) - 0.5) * uniform.uBandWidth * 0.4;
    let effectiveDist = g_dist + jitter;

    // --- Forward wave (inside-out) ---
    var forwardBloom: f32;
    if (effectiveDist > wavePos + uniform.uBandWidth * 0.5) {
        forwardBloom = 0.0;
    } else if (effectiveDist > wavePos - uniform.uBandWidth * 0.5) {
        forwardBloom = smoothstep(wavePos + uniform.uBandWidth * 0.5, wavePos - uniform.uBandWidth * 0.5, effectiveDist);
    } else {
        let distBehind = (wavePos - uniform.uBandWidth * 0.5) - effectiveDist;
        let settleTime = distBehind / max(uniform.uWaveSpeed + uniform.uAcceleration * uniform.uTime, 1.0);
        let overshootDecay = exp(-settleTime * 6.0);
        forwardBloom = 1.0 + (uniform.uOvershoot - 1.0) * overshootDecay;
    }

    // --- Reverse wave (outside-in) for outer 10% of splats ---
    var reverseBloom: f32 = 0.0;
    let outerThreshold = uniform.uWarpedRadius * 0.9;
    if (g_dist > outerThreshold && uniform.uWarpedRadius > 0.0) {
        let distFromEdge = uniform.uWarpedRadius - effectiveDist;
        let revWavePos = uniform.uWaveSpeed * uniform.uTime + 0.5 * uniform.uAcceleration * uniform.uTime * uniform.uTime;
        if (distFromEdge < revWavePos + uniform.uBandWidth * 0.5) {
            if (distFromEdge > revWavePos - uniform.uBandWidth * 0.5) {
                reverseBloom = smoothstep(revWavePos + uniform.uBandWidth * 0.5, revWavePos - uniform.uBandWidth * 0.5, distFromEdge);
            } else {
                let distBehind = (revWavePos - uniform.uBandWidth * 0.5) - distFromEdge;
                let settleTime = distBehind / max(uniform.uWaveSpeed + uniform.uAcceleration * uniform.uTime, 1.0);
                let overshootDecay = exp(-settleTime * 6.0);
                reverseBloom = 1.0 + (uniform.uOvershoot - 1.0) * overshootDecay;
            }
        }
    }

    g_bloom = max(forwardBloom, reverseBloom);
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
    initBloom(*center);
    rl_casterCenter(center);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    rl_recordSplatShape(*rotation, *scale);
    mirrorClipSplat(modifiedCenter, scale);
    if (g_bloom < 0.01) {
        *scale = vec3f(0.0);
        return;
    }

    if (g_bloom >= 0.99 && g_bloom <= 1.01) {
        return;
    }

    let origSize = gsplatGetSizeFromScale(*scale);

    if (g_bloom <= 1.0) {
        let t = g_bloom;

        if (t < 0.15) {
            let dotSize = min(t * 0.4 * origSize, origSize);
            gsplatMakeSpherical(scale, dotSize);
        } else if (t < 0.5) {
            let size = mix(0.06, 0.5, (t - 0.15) / 0.35) * origSize;
            gsplatMakeSpherical(scale, min(size, origSize));
        } else {
            let morphT = (t - 0.5) / 0.5;
            let sizeFactor = mix(0.5, 1.0, morphT);
            *scale *= sizeFactor;
        }
    } else {
        *scale *= g_bloom;
    }
}

fn bloomColorEffect(center: vec3f, color: ptr<function, vec4f>) {
    if (g_bloom < 0.01) {
        return;
    }

    if (g_bloom <= 1.0) {
        let waveFrontIntensity = sin(clamp(g_bloom, 0.0, 1.0) * 3.14159);
        (*color) = vec4f((*color).rgb + uniform.uBloomTint * waveFrontIntensity * 0.5, (*color).a);
    } else if (g_bloom > 1.005) {
        let overshootAmount = (g_bloom - 1.0) / max(uniform.uOvershoot - 1.0, 0.01);
        (*color) = vec4f((*color).rgb + uniform.uSettleTint * overshootAmount * 0.3, (*color).a);
    }
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    // EFFECT_HOOK
    bloomColorEffect(center, color);
}
`;

// Cache for the script class
let _GsplatRevealBloomClass: typeof pc.ScriptType | null = null;

type ScriptTypeWithPrototype<T extends pc.ScriptType> = typeof pc.ScriptType & {
    prototype: T;
};

export interface GsplatRevealBloomScript extends pc.ScriptType {
    effectTime: number;
    material: pc.Material | null;
    _effectInitialized: boolean;
    _shadersNeedApplication: boolean;
    // Same external interface as radial reveal for drop-in compatibility
    center: pc.Vec3;
    speed: number;
    acceleration: number;
    delay: number;              // unused, kept for interface compat
    oscillationIntensity: number; // reinterpreted as overshoot (0.2 → 1.2x overshoot)
    dotTint: pc.Color;          // bloom wavefront tint
    waveTint: pc.Color;         // settle/overshoot tint
    endRadius: number;          // fallback; auto-detected from AABB when available
    _autoEndRadius: number;     // computed from gsplat AABB
    _bloomTintArray: [number, number, number];
    _settleTintArray: [number, number, number];
    _centerArray: [number, number, number];
    _detectSceneRadius: () => void;
    _getCompletionTime: () => number;
    _isEffectComplete: () => boolean;
    getShaderGLSL: () => string;
    getShaderWGSL: () => string;
    _applyShaders: () => void;
    _applyShaderToMaterial: (material: pc.Material) => void;
    _removeShaders: () => void;
    _updateUniforms: () => void;
    _setUniform: (name: string, value: number | number[] | Float32Array) => void;
}

/**
 * Gets or creates the GsplatRevealBloom script class.
 * Must be called after PlayCanvas app is initialized.
 */
export function getGsplatRevealBloomClass(): typeof pc.ScriptType {
    if (_GsplatRevealBloomClass) {
        return _GsplatRevealBloomClass;
    }

    const GsplatRevealBloom = pc.createScript('gsplatRevealBloom') as ScriptTypeWithPrototype<GsplatRevealBloomScript>;

    Object.assign(GsplatRevealBloom.prototype, {
        effectTime: 0,
        material: null as pc.Material | null,
        _effectInitialized: false,
        _shadersNeedApplication: false,
        _bloomTintArray: [0, 0, 0] as [number, number, number],
        _settleTintArray: [0, 0, 0] as [number, number, number],
        _centerArray: [0, 0, 0] as [number, number, number],

        // External interface (same property names as radial)
        center: null as pc.Vec3 | null,
        speed: 100,               // faster default than radial (4x original)
        acceleration: 32,         // strong acceleration for punch (4x original)
        delay: 0,                 // not used in bloom
        oscillationIntensity: 0.2, // reinterpreted: overshoot = 1 + this
        dotTint: null as pc.Color | null,
        waveTint: null as pc.Color | null,
        endRadius: 500,
        _autoEndRadius: 0,

        initialize(this: GsplatRevealBloomScript) {
            this.effectTime = 0;
            this.material = null;
            this._effectInitialized = false;
            this._shadersNeedApplication = false;
            this._bloomTintArray = [0, 0, 0];
            this._settleTintArray = [0, 0, 0];
            this._centerArray = [0, 0, 0];

            if (!this.center) this.center = new pc.Vec3(0, 0, 0);
            if (!this.dotTint) this.dotTint = new pc.Color(1.0, 0.6, 0.15);   // warm amber
            if (!this.waveTint) this.waveTint = new pc.Color(0.2, 0.5, 1.0);  // cool blue

            this.on('enable', () => {
                this.effectTime = 0;
                if (this._effectInitialized) {
                    this._applyShaders();
                } else {
                    this._shadersNeedApplication = true;
                }
            });

            this.on('disable', () => {
                this._removeShaders();
            });

            if (!this.entity.gsplat) {
                return;
            }

            this._effectInitialized = true;
            this._detectSceneRadius();
            if (this.enabled) {
                this._applyShaders();
            }
        },

        update(this: GsplatRevealBloomScript, dt: number) {
            if (!this._effectInitialized) {
                if (this.entity.gsplat) {
                    this._effectInitialized = true;
                    this._detectSceneRadius();
                    if (this.enabled && this._shadersNeedApplication) {
                        this._applyShaders();
                        if (this.material) {
                            this._shadersNeedApplication = false;
                        }
                    }
                }
                return;
            }

            if (this._shadersNeedApplication) {
                this._applyShaders();
                if (this.material) {
                    this._shadersNeedApplication = false;
                }
            }

            if (!this.material) return;

            this.effectTime += dt;

            if (this._isEffectComplete()) {
                console.log('[BloomReveal] Effect complete, disabling');
                this.enabled = false;
                return;
            }

            this._updateUniforms();
            this.material.update();
        },

        _detectSceneRadius(this: GsplatRevealBloomScript) {
            try {
                const gsplat = this.entity.gsplat;
                const inst = gsplat && (gsplat as any).instance;
                const mi = inst?.meshInstance ?? inst?.meshInstances?.[0];
                const aabb = mi?.aabb;
                if (aabb) {
                    const he = aabb.halfExtents;
                    const r = Math.sqrt(he.x * he.x + he.y * he.y + he.z * he.z);
                    // Add 20% buffer so the wave fully clears the edges
                    this._autoEndRadius = r * 1.2;
                }
            } catch (_e) {
                // AABB not available yet; will use endRadius fallback
            }
        },

        _getCompletionTime(this: GsplatRevealBloomScript): number {
            // Use auto-detected scene radius if available, otherwise fallback
            const radius = this._autoEndRadius > 0 ? this._autoEndRadius : this.endRadius;
            // Apply the same distance warp as the shader so auto-disable fires at the
            // moment the warped wave actually reaches the farthest splat.
            // Must match bloomShaderGLSL/WGSL: warped = raw / sqrt(1 + raw/K), K=5.
            const K = 5.0;
            const warpedRadius = radius / Math.sqrt(1.0 + radius / K);
            if (this.acceleration === 0) {
                return (warpedRadius / this.speed) + 0.5;
            }
            const discriminant = this.speed * this.speed + 2 * this.acceleration * warpedRadius;
            if (discriminant < 0) return Infinity;
            const t = (-this.speed + Math.sqrt(discriminant)) / this.acceleration;
            return t + 0.5;
        },

        _isEffectComplete(this: GsplatRevealBloomScript): boolean {
            return this.effectTime >= this._getCompletionTime();
        },

        _updateUniforms(this: GsplatRevealBloomScript) {
            this._setUniform('uTime', this.effectTime);
            this._centerArray[0] = this.center!.x;
            this._centerArray[1] = this.center!.y;
            this._centerArray[2] = this.center!.z;
            this._setUniform('uCenter', this._centerArray);
            this._setUniform('uWaveSpeed', this.speed);
            this._setUniform('uAcceleration', this.acceleration);
            this._setUniform('uBandWidth', 3.0); // fixed band width for clean look
            // oscillationIntensity reinterpreted as overshoot amount
            this._setUniform('uOvershoot', 1.0 + Math.max(0, this.oscillationIntensity));
            // Warped radius for the reverse wave (outer 10% reveals from outside-in)
            const radius = this._autoEndRadius > 0 ? this._autoEndRadius : this.endRadius;
            const K = 5.0;
            this._setUniform('uWarpedRadius', radius / Math.sqrt(1.0 + radius / K));
            this._bloomTintArray[0] = this.dotTint!.r;
            this._bloomTintArray[1] = this.dotTint!.g;
            this._bloomTintArray[2] = this.dotTint!.b;
            this._setUniform('uBloomTint', this._bloomTintArray);
            this._settleTintArray[0] = this.waveTint!.r;
            this._settleTintArray[1] = this.waveTint!.g;
            this._settleTintArray[2] = this.waveTint!.b;
            this._setUniform('uSettleTint', this._settleTintArray);
        },

        getShaderGLSL(): string {
            return bloomShaderGLSL;
        },

        getShaderWGSL(): string {
            return bloomShaderWGSL;
        },

        _applyShaders(this: GsplatRevealBloomScript) {
            const gsplat = this.entity.gsplat;
            if (!gsplat) return;

            if ((gsplat as any).unified) {
                this.material = (this.app.scene as any).gsplat?.material ?? null;
                if (!this.material) {
                    this._shadersNeedApplication = true;
                    return;
                }
                this._applyShaderToMaterial(this.material);
            } else {
                const mat = gsplat.material;
                if (mat) {
                    this.material = mat;
                    this._applyShaderToMaterial(mat);
                } else {
                    this._shadersNeedApplication = true;
                }
            }
        },

        _applyShaderToMaterial(this: GsplatRevealBloomScript, material: pc.Material) {
            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
            let shader = lang === 'wgsl' ? this.getShaderWGSL() : this.getShaderGLSL();

            // Check for sibling relighting script - if present, combine shaders
            const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
            if (relightScript && relightScript.enabled) {
                // Tell relighting script not to manage the shader chunk itself
                // Must be set BEFORE the combined shader is applied to avoid race with relighting's on('enable')
                relightScript._shaderManagedExternally = true;

                // relightingSnippet already includes mirrorClipSnippet, so no separate prepend needed
                const snippet = lang === 'wgsl' ? relightingSnippetWGSL : relightingSnippetGLSL;
                // Replace the EFFECT_HOOK sentinel with relighting call before the effect call
                shader = snippet + '\n' + shader.replace(
                    '// EFFECT_HOOK',
                    'applyRelighting(center, color);'
                );
            } else {
                // Standalone: prepend mirror clip snippet so mirrorClipSplat() is defined
                const mirrorSnippet = lang === 'wgsl' ? mirrorClipSnippetWGSL : mirrorClipSnippetGLSL;
                shader = mirrorSnippet + shader;
            }

            material.getShaderChunks(lang).set('gsplatModifyVS', shader);
            material.update();
        },

        _removeShaders(this: GsplatRevealBloomScript) {
            if (!this.material) return;

            const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
            if (relightScript && relightScript.enabled) {
                relightScript.material = this.material;
                const device = this.app.graphicsDevice;
                const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
                const relightShader = lang === 'wgsl' ? relightScript.getShaderWGSL() : relightScript.getShaderGLSL();
                this.material.getShaderChunks(lang).set('gsplatModifyVS', relightShader);
                this.material.update();
                // Release external management AFTER shader is applied to avoid race
                relightScript._shaderManagedExternally = false;
                this.material = null;
                return;
            }

            // Check for compare clip script — if active, restore its shader instead of deleting
            const compareClipScript = this.entity.script && (this.entity.script as any).gsplatCompareClip;
            if (compareClipScript && compareClipScript.enabled) {
                // Force compare clip to reapply its shader on next frame
                compareClipScript.material = null;
                compareClipScript._shadersNeedApplication = true;
                this.material = null;
                return;
            }

            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
            this.material.getShaderChunks(lang).delete('gsplatModifyVS');
            this.material.update();
            this.material = null;
        },

        _setUniform(this: GsplatRevealBloomScript, name: string, value: number | number[] | Float32Array) {
            this.material?.setParameter(name, value);
        },

        destroy(this: GsplatRevealBloomScript) {
            this._removeShaders();
        }
    });

    _GsplatRevealBloomClass = GsplatRevealBloom;
    return GsplatRevealBloom;
}

export const GsplatRevealBloom = {
    get class() {
        return getGsplatRevealBloomClass();
    }
};
